import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, Sparkles, Heart, Shield, Loader2 } from 'lucide-react';

const YouthPortalLogin = ({ onLoginSuccess, backendUrl }) => {
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${backendUrl}/api/youth-portal/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, pin })
      });

      const data = await response.json();

      if (data.success) {
        // Store youth session
        sessionStorage.setItem('youth_token', data.access_token);
        sessionStorage.setItem('youth_client_id', data.client_id);
        sessionStorage.setItem('youth_first_name', data.first_name);
        sessionStorage.setItem('youth_age_tier', data.age_tier);
        sessionStorage.setItem('youth_features', JSON.stringify(data.features));
        
        onLoginSuccess(data);
      } else {
        setError(data.detail || 'Invalid username or PIN');
      }
    } catch (err) {
      setError('Unable to connect. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 mb-4">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            CounselCore
          </h1>
          <p className="text-gray-600 mt-2">Your Safe Space</p>
        </div>

        {/* Login Card */}
        <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl text-gray-800">Welcome Back! 👋</CardTitle>
            <CardDescription>Enter your username and PIN to continue</CardDescription>
          </CardHeader>
          
          <CardContent className="pt-4">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-gray-700">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  className="text-lg py-6 border-purple-200 focus:border-purple-500"
                  required
                  data-testid="youth-username-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="pin" className="text-gray-700">PIN</Label>
                <Input
                  id="pin"
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="Enter your 4-digit PIN"
                  className="text-lg py-6 text-center tracking-widest border-purple-200 focus:border-purple-500"
                  required
                  data-testid="youth-pin-input"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg text-red-600">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || !username || pin.length !== 4}
                className="w-full py-6 text-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                data-testid="youth-login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Signing In...
                  </>
                ) : (
                  "Let's Go! 🚀"
                )}
              </Button>
            </form>

            {/* Help Section */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-center text-sm text-gray-500">
                Forgot your PIN? Ask your therapist or parent for help.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Safety Message */}
        <div className="mt-6 flex items-center justify-center gap-2 text-gray-600">
          <Shield className="w-4 h-4" />
          <span className="text-sm">Your information is safe and private</span>
        </div>

        {/* Crisis Resources Link */}
        <div className="mt-4 text-center">
          <a
            href="tel:988"
            className="inline-flex items-center gap-2 text-sm text-purple-600 hover:text-purple-800"
          >
            <Heart className="w-4 h-4" />
            Need to talk to someone right now? Call or text 988
          </a>
        </div>
      </div>
    </div>
  );
};

export default YouthPortalLogin;
